// ConfigContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { API_URL } from '../api/config';  // Certifique-se que o API_URL está definido

export interface Config {
  darkMode: boolean;
  language: string;
  showRoomData: boolean;
  showUserData: boolean;
  qrCodeSearchAction: string;
  autoOpenQRCodeScanner: boolean;
  showConfirmation: boolean;
  returnToAccessAfterAction: boolean;
  allowMultipleEntries: boolean;
}

const defaultConfig: Config = {
  darkMode: false,
  language: 'pt-BR',
  showRoomData: true,
  showUserData: true,
  qrCodeSearchAction: 'presentUserData',
  autoOpenQRCodeScanner: true,
  showConfirmation: true,
  returnToAccessAfterAction: true,
  allowMultipleEntries: false,
};

interface ConfigContextProps {
  config: Config;
  setConfig: (config: Config) => void;
}

const ConfigContext = createContext<ConfigContextProps>({
  config: defaultConfig,
  setConfig: () => {},
});

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<Config>(defaultConfig);

  // Inicializa o estado com o localStorage, se disponível,
  // caso contrário, faz o fetch do backend via batch.
  useEffect(() => {
    const fetchBatchConfigs = async () => {
      const keys = [
        "darkMode",
        "language",
        "showRoomData",
        "showUserData",
        "qrCodeSearchAction",
        "autoOpenQRCodeScanner",
        "showConfirmation",
        "returnToAccessAfterAction",
        "allowMultipleEntries"
      ];
      try {
        const res = await fetch(`${API_URL}/system-config/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keys }),
        });
        if (res.ok) {
          const data: { [key: string]: string } = await res.json();
          const newConfig: Config = {
            darkMode: data.darkMode === 'true',
            language: data.language,
            showRoomData: data.showRoomData === 'true',
            showUserData: data.showUserData === 'true',
            qrCodeSearchAction: data.qrCodeSearchAction,
            autoOpenQRCodeScanner: data.autoOpenQRCodeScanner === 'true',
            showConfirmation: data.showConfirmation === 'true',
            returnToAccessAfterAction: data.returnToAccessAfterAction === 'true',
            allowMultipleEntries: data.allowMultipleEntries === 'true',
          };
          setConfig(newConfig);
          localStorage.setItem('systemConfig', JSON.stringify(newConfig));
        } else {
          console.error("Erro ao buscar configurações em batch:", res.statusText);
        }
      } catch (err) {
        console.error("Erro ao buscar configurações em batch:", err);
      }
    };

    // Sempre busca e atualiza as configurações do servidor
    fetchBatchConfigs();
  }, []);


  // Persistência: sempre que o config mudar, salva no localStorage.
  useEffect(() => {
    localStorage.setItem('systemConfig', JSON.stringify(config));
  }, [config]);

  return (
    <ConfigContext.Provider value={{ config, setConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);
